import React, { useMemo } from 'react';
import { Box, Typography, IconButton, Stack, Tooltip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PublicIcon from '@mui/icons-material/Public';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';

import { useInstatDesign } from './theme/instatDesign';
import { Card, KpiTile, Pill } from './ui';

// -- helpers ----------------------------------------------------------------

// ISO-3 → flag emoji. Falls back to ISO-2 short (citizenship.short from Hudl).
function flagEmoji(code) {
  if (!code) return '🏳️';
  const s = String(code).toUpperCase();
  // Hudl usually returns 3-letter short like "MNE" — convert to 2 via common map
  const iso3to2 = { MNE: 'ME', SRB: 'RS', ESP: 'ES', FRA: 'FR', USA: 'US', GER: 'DE', ITA: 'IT',
    GBR: 'GB', LTU: 'LT', LVA: 'LV', EST: 'EE', POL: 'PL', GRC: 'GR', TUR: 'TR', ARG: 'AR',
    BRA: 'BR', CAN: 'CA', MEX: 'MX', AUS: 'AU', NGA: 'NG', SEN: 'SN', CMR: 'CM', DOM: 'DO',
    CRO: 'HR', HRV: 'HR', SLO: 'SI', SVN: 'SI', CZE: 'CZ', SVK: 'SK', UKR: 'UA', RUS: 'RU',
    CHN: 'CN', JPN: 'JP', KOR: 'KR', ISR: 'IL', PHI: 'PH', NZL: 'NZ', BEL: 'BE', NLD: 'NL',
    SWE: 'SE', NOR: 'NO', FIN: 'FI', DNK: 'DK', AUT: 'AT', CHE: 'CH', POR: 'PT', PRT: 'PT',
    MKD: 'MK', ROU: 'RO', BGR: 'BG', HUN: 'HU', EGY: 'EG', TUN: 'TN', ALG: 'DZ', MLI: 'ML' };
  let iso2 = s.length === 2 ? s : iso3to2[s];
  if (!iso2 || iso2.length !== 2) return '🌍';
  return iso2.replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

function initials(name) {
  if (!name) return 'P';
  return name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

// Compute a lightweight trend arrow vs prev-season numeric.
// Both values are expected as raw numbers. Returns `{direction, delta}` or null.
function computeTrend(curr, prev, unit = '') {
  if (curr == null || prev == null) return null;
  const c = Number(curr);
  const p = Number(prev);
  if (!Number.isFinite(c) || !Number.isFinite(p)) return null;
  const diff = c - p;
  const abs = Math.abs(diff);
  if (abs < 0.05) return { direction: 'flat', delta: '' };
  const sign = diff > 0 ? '+' : '';
  const formatted = abs >= 10 ? diff.toFixed(0) : diff.toFixed(1);
  return { direction: diff > 0 ? 'up' : 'down', delta: `${sign}${formatted}${unit}` };
}

// Match on Hudl statparam IDs (stable catalog), not on lexicon labels which
// vary between seasons / competitions.
//   p=2  MIN       p=3  PTS       p=6  FG%      p=9  3P%
//   p=15 REB       p=16 AST
function pickStatByParam(stats, paramIds) {
  if (!Array.isArray(stats)) return null;
  for (const row of stats) {
    if (paramIds.includes(Number(row.statparam))) return row;
  }
  return null;
}

// ----------------------------------------------------------------------------

export default function PlayerHeroCard({
  info,
  playerId,
  currStats, // array from overview_player_stat (curr season)
  prevStats, // array from overview_player_stat (prev season)
  sx = {},
}) {
  const d = useInstatDesign();

  // Build the 6 hero KPI tiles — match by statparam (stable), not label.
  const kpis = useMemo(() => {
    const pick = (ids) => pickStatByParam(currStats, ids);
    const prev = (ids) => pickStatByParam(prevStats, ids);

    const cfg = [
      { key: 'PTS',  params: [3],  unit: '' },
      { key: 'REB',  params: [15], unit: '' },
      { key: 'AST',  params: [16], unit: '' },
      { key: 'FG%',  params: [6],  unit: '%' },
      { key: '3P%',  params: [9],  unit: '%' },
      { key: 'MIN',  params: [2],  unit: '' },
    ];

    return cfg.map((c) => {
      const row = pick(c.params);
      const prevRow = prev(c.params);
      return {
        key: c.key,
        label: c.key,
        value: row?.value ?? '—',
        trend: computeTrend(row?.raw, prevRow?.raw, c.unit),
      };
    });
  }, [currStats, prevStats]);

  const age = info?.age;
  const height = info?.height;
  const weight = info?.weight;
  const nameMain = info?.name || `Player ${playerId}`;

  return (
    <Card
      padding={{ xs: 2.5, md: 3 }}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        ...sx,
      }}
    >
      {/* Subtle accent blob in bg */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: -80,
          right: -80,
          width: 260,
          height: 260,
          borderRadius: '50%',
          background: d.accentGradient,
          opacity: 0.08,
          filter: 'blur(8px)',
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'relative', display: 'flex', gap: { xs: 2, md: 3 }, flexWrap: 'wrap' }}>
        {/* Photo */}
        <Box
          sx={{
            width: { xs: 120, md: 148 },
            height: { xs: 120, md: 148 },
            borderRadius: `${d.radius.xl}px`,
            bgcolor: d.colors.canvasAlt,
            overflow: 'hidden',
            flexShrink: 0,
            boxShadow: d.shadows.card,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundImage: info?.photo ? `url(${info.photo})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
          }}
        >
          {!info?.photo && (
            <Typography
              sx={{
                fontSize: 48,
                fontWeight: 700,
                color: d.colors.textTertiary,
                letterSpacing: '-0.04em',
              }}
            >
              {initials(nameMain)}
            </Typography>
          )}
          {info?.number > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: d.colors.surface,
                borderRadius: `${d.radius.sm}px`,
                px: 0.75,
                py: 0.25,
                fontSize: 12,
                fontWeight: 700,
                color: d.colors.text,
                boxShadow: d.shadows.card,
                ...d.typo.tabular,
              }}
            >
              #{info.number}
            </Box>
          )}
        </Box>

        {/* Identity & chips */}
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  ...d.typo.display,
                  fontSize: { xs: 28, sm: 34, md: 40 },
                  color: d.colors.text,
                  mb: 0.25,
                  wordBreak: 'break-word',
                }}
              >
                {nameMain}
              </Typography>
              <Typography sx={{ ...d.typo.body, color: d.colors.textSecondary, mb: 1.5 }}>
                {info?.position?.name || '—'}
                {info?.team?.name ? ` · ${info.team.name}` : ''}
              </Typography>
            </Box>

            <Tooltip title="Open on Hudl">
              <IconButton
                size="small"
                component="a"
                href={`https://app.hudl.com/instat/basketball/players/${playerId}`}
                target="_blank"
                rel="noopener"
                sx={{ color: d.colors.textSecondary }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            {info?.team?.name && (
              <Pill tone="accent" icon={<SportsBasketballIcon sx={{ fontSize: 13 }} />}>
                {info.team.short || info.team.name}
              </Pill>
            )}
            {info?.citizenship?.short && (
              <Pill tone="neutral">
                <Box component="span" sx={{ fontSize: 14, mr: 0.25 }}>
                  {flagEmoji(info.citizenship.short)}
                </Box>
                {info.citizenship.name || info.citizenship.short}
              </Pill>
            )}
            {info?.national_team?.name && (
              <Pill tone="outline" icon={<PublicIcon sx={{ fontSize: 13 }} />}>
                {info.national_team.name}
              </Pill>
            )}
            {age != null && <Pill tone="neutral">{age} y</Pill>}
            {height && <Pill tone="neutral">{height}</Pill>}
            {weight && <Pill tone="neutral">{weight}</Pill>}
            <Pill tone="outline" size="sm" sx={{ opacity: 0.7 }}>
              ID {playerId}
            </Pill>
          </Stack>
        </Box>
      </Box>

      {/* KPI strip */}
      <Box
        sx={{
          mt: { xs: 2.5, md: 3 },
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(3, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(6, 1fr)',
          },
          gap: 1.25,
        }}
      >
        {kpis.map((k) => (
          <KpiTile
            key={k.key}
            label={k.label}
            value={k.value}
            trend={k.trend}
            accent={k.key === 'PTS'}
            compact
          />
        ))}
      </Box>
    </Card>
  );
}
